/**
 * Edit modal for an existing preset.
 *
 * Pre-seeds every field from the loaded {@link Preset} (the parent passes
 * the already-fetched record so we don't re-hit the API). On submit we ONLY
 * send fields the operator actually touched, matching the API's optional
 * `PresetUpdateRequest` semantics - leaving a field absent means "no change"
 * server-side.
 *
 * Manager-list editing is hidden when `canEditManagers` is false (manager-
 * only caller; V6 would 403 anyway, but we don't want to dangle a control
 * the caller can't use).
 *
 * Layout mirrors CreatePresetModal (2026-06 redesign): a wide modal with a
 * left section-rail (scroll-spy) and a scrolling pane of sections - Details,
 * Recipients, Administration. There is no Owner field (a preset's owner is
 * fixed), so the first section is just the names.
 */
import { useEffect, useRef, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonPopover,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  chevronDownOutline,
  closeOutline,
  documentTextOutline,
  informationCircleOutline,
  lockClosedOutline,
  peopleOutline,
} from "ionicons/icons";
import { displayMsg } from "pymble-ui/components";
import { useUpdatePreset } from "../../hooks/usePresets";
import { ContactPicker } from "./ContactPicker";
import { ConstituentGroupPicker } from "./ConstituentGroupPicker";
import { parsePresetError } from "./presetErrorParser";
import type { Preset, PresetUpdateRequest } from "../../types/preset";

import "./presets.css";

interface EditPresetModalProps {
  preset: Preset;
  canEditManagers: boolean;
  onDismiss: () => void;
  onSaved: () => void;
}

interface RailSection {
  id: string;
  step: number;
  icon: string;
  title: string;
  subtitle: string;
}

const SECTIONS: RailSection[] = [
  {
    id: "details",
    step: 1,
    icon: documentTextOutline,
    title: "Details",
    subtitle: "Update the preset's name",
  },
  {
    id: "recipients",
    step: 2,
    icon: peopleOutline,
    title: "Recipients",
    subtitle: "Choose who should receive this preset",
  },
  {
    id: "administration",
    step: 3,
    icon: lockClosedOutline,
    title: "Administration",
    subtitle: "Managers and privacy settings",
  },
];

export function EditPresetModal({
  preset,
  canEditManagers,
  onDismiss,
  onSaved,
}: EditPresetModalProps) {
  const update = useUpdatePreset(preset.id);

  const [nameShort, setNameShort] = useState(preset.name_short);
  const [nameLong, setNameLong] = useState(preset.name_long);
  const [constituentIds, setConstituentIds] = useState<string[]>(
    preset.definition.constituent_group_ids
  );
  const [individualIds, setIndividualIds] = useState<string[]>(
    preset.definition.individual_contact_ids
  );
  const [managerIds, setManagerIds] = useState<number[]>(
    preset.manager_contact_ids
  );
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [errorBullets, setErrorBullets] = useState<string[]>([]);
  // Editors can already have managers set, so default the section open when
  // it's theirs to edit (the toggle still lets them collapse it).
  const [adminOpen, setAdminOpen] = useState(canEditManagers);
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Re-seed if the parent feeds a fresher preset (refetch after edit).
  useEffect(() => {
    setNameShort(preset.name_short);
    setNameLong(preset.name_long);
    setConstituentIds(preset.definition.constituent_group_ids);
    setIndividualIds(preset.definition.individual_contact_ids);
    setManagerIds(preset.manager_contact_ids);
  }, [preset]);

  // Scroll-spy: mark the section nearest the top of the scroll pane active.
  // Guarded for jsdom/SSR where IntersectionObserver is absent.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const top = visible[0]?.target as HTMLElement | undefined;
        if (top?.dataset.sectionId) setActiveId(top.dataset.sectionId);
      },
      { root, threshold: [0.2, 0.5, 0.75], rootMargin: "0px 0px -55% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goToSection = (id: string) => {
    setActiveId(id);
    sectionRefs.current[id]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleSubmit = async () => {
    setErrorTitle(null);
    setErrorBullets([]);

    if (!nameShort.trim()) {
      setErrorTitle("Name (short) is required");
      goToSection("details");
      return;
    }

    const body: PresetUpdateRequest = {};
    if (nameShort.trim() !== preset.name_short) {
      body.name_short = nameShort.trim();
    }
    if (nameLong.trim() !== preset.name_long) {
      body.name_long = nameLong.trim();
    }
    // Membership: always re-send the full definition when EITHER list
    // changed, since the API treats `definition` as a complete replacement.
    const constituentChanged =
      JSON.stringify(constituentIds) !==
      JSON.stringify(preset.definition.constituent_group_ids);
    const individualChanged =
      JSON.stringify(individualIds) !==
      JSON.stringify(preset.definition.individual_contact_ids);
    if (constituentChanged || individualChanged) {
      body.definition = {
        constituent_group_ids: constituentIds,
        individual_contact_ids: individualIds,
      };
    }
    // Manager list: only send when the operator is permitted to edit it
    // AND has actually changed something. V6 would 403 a manager-only
    // caller attempting to send manager_contact_ids; the UI guard avoids
    // the wasted round-trip.
    if (canEditManagers) {
      const managersChanged =
        JSON.stringify(managerIds) !==
        JSON.stringify(preset.manager_contact_ids);
      if (managersChanged) {
        body.manager_contact_ids = managerIds;
      }
    }

    if (Object.keys(body).length === 0) {
      onDismiss();
      return;
    }

    try {
      await update.mutateAsync(body);
      displayMsg("Preset saved", {
        type: "success",
        position: "top",
        duration: 1500,
      });
      onSaved();
    } catch (err) {
      const parsed = parsePresetError(err);
      if (parsed) {
        setErrorTitle(parsed.title);
        setErrorBullets(parsed.bullets);
      } else {
        setErrorTitle("Failed to save preset - please try again");
      }
    }
  };

  const isSubmitting = update.isPending;

  // Seed labels for chips so edit-mode users see real names immediately.
  const individualLabels = preset.definition.individual_contact_labels ?? {};
  const managerLabels = preset.manager_contact_labels ?? {};

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Edit preset</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={onDismiss}>
              Cancel
            </IonButton>
            <IonButton
              fill="clear"
              onClick={onDismiss}
              aria-label="Close"
              className="preset-modal-close"
            >
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false} className="preset-create-content">
        <div className="preset-create-layout">
          <nav className="preset-rail" aria-label="Preset sections">
            {SECTIONS.map((s) => {
              const isActive = activeId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={
                    isActive
                      ? "preset-rail-step preset-rail-step--active"
                      : "preset-rail-step"
                  }
                  aria-current={isActive ? "step" : undefined}
                  onClick={() => goToSection(s.id)}
                >
                  <span className="preset-rail-badge">{s.step}</span>
                  <span className="preset-rail-text">
                    <span className="preset-rail-title">
                      <IonIcon icon={s.icon} aria-hidden="true" />
                      {s.title}
                    </span>
                    <span className="preset-rail-subtitle">{s.subtitle}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="preset-create-sections" ref={scrollRef}>
            {errorTitle && (
              <div className="preset-modal-error" role="alert">
                <strong>{errorTitle}</strong>
                {errorBullets.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {errorBullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* ---- Details ---- */}
            <section
              className="preset-section"
              data-section-id="details"
              ref={(el) => (sectionRefs.current.details = el)}
            >
              <h2 className="preset-section-title">Details</h2>
              <div className="preset-grid-2">
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Name (short)
                      <span className="preset-required" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </span>
                    <IonInput
                      className="preset-text-input"
                      fill="outline"
                      aria-label="Name (short)"
                      value={nameShort}
                      onIonInput={(e) => setNameShort(e.detail.value ?? "")}
                      maxlength={120}
                    />
                  </div>
                </div>
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Name (long)
                    </span>
                    <IonInput
                      className="preset-text-input"
                      fill="outline"
                      aria-label="Name (long)"
                      value={nameLong}
                      onIonInput={(e) => setNameLong(e.detail.value ?? "")}
                      maxlength={240}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* ---- Recipients ---- */}
            <section
              className="preset-section"
              data-section-id="recipients"
              ref={(el) => (sectionRefs.current.recipients = el)}
            >
              <div className="preset-section-head">
                <h2 className="preset-section-title">Recipients</h2>
                <IonButton
                  id="edit-preset-diff-info"
                  fill="clear"
                  size="small"
                  className="preset-diff-link"
                >
                  What is the difference?
                  <IonIcon slot="end" icon={informationCircleOutline} />
                </IonButton>
                <IonPopover
                  trigger="edit-preset-diff-info"
                  triggerAction="click"
                >
                  <div className="preset-popover-content">
                    <p>
                      <strong>Constituent groups</strong> add whole groups -
                      every current member is merged into the preset's roster,
                      and the roster reflects membership changes the next time
                      the preset is materialised.
                    </p>
                    <p>
                      <strong>Individual contacts</strong> add specific people
                      on top of the groups - use this for one-off recipients who
                      aren't in any constituent group.
                    </p>
                  </div>
                </IonPopover>
              </div>
              <div className="preset-grid-2">
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Constituent groups
                    </span>
                    <span className="preset-modal-field-hint">
                      Members of these groups are merged into the preset's
                      roster.
                    </span>
                    <ConstituentGroupPicker
                      collapsible
                      selectedIds={constituentIds}
                      onChange={setConstituentIds}
                      initialLabels={
                        preset.definition.constituent_group_labels
                      }
                    />
                  </div>
                </div>
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Individual contacts
                    </span>
                    <span className="preset-modal-field-hint">
                      Add specific people who should also receive the preset.
                    </span>
                    <ContactPicker
                      mode="prefixed"
                      collapsible
                      countNoun="contact"
                      showAvatars
                      selectedIds={individualIds}
                      onChange={setIndividualIds}
                      initialLabels={individualLabels}
                      placeholder="Search staff, students, carers..."
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* ---- Administration ---- */}
            <section
              className="preset-section"
              data-section-id="administration"
              ref={(el) => (sectionRefs.current.administration = el)}
            >
              <div className="preset-section-head">
                <h2 className="preset-section-title">Administration</h2>
                {canEditManagers && (
                  <IonButton
                    fill="outline"
                    size="small"
                    className="preset-admin-toggle"
                    aria-expanded={adminOpen}
                    onClick={() => setAdminOpen((o) => !o)}
                  >
                    {adminOpen ? "Collapse" : "Expand"}
                    <IonIcon
                      slot="end"
                      icon={chevronDownOutline}
                      className={
                        adminOpen
                          ? "preset-admin-toggle-icon open"
                          : "preset-admin-toggle-icon"
                      }
                    />
                  </IonButton>
                )}
              </div>

              {canEditManagers ? (
                adminOpen && (
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">Managers</span>
                    <span className="preset-modal-field-hint">
                      Managers can edit content but cannot change this list or
                      delete the preset.
                    </span>
                    <ContactPicker
                      mode="numeric"
                      collapsible
                      countNoun="manager"
                      selectedIds={managerIds}
                      onChange={setManagerIds}
                      initialLabels={managerLabels}
                      placeholder="Search staff..."
                      types={["staff"]}
                      unknownLabel="Manager"
                    />
                  </div>
                )
              ) : (
                <p className="preset-modal-field-hint preset-admin-blurb">
                  Only the preset's owner can change the manager list.
                </p>
              )}

              <div className="preset-privacy-banner">
                <IonIcon icon={lockClosedOutline} aria-hidden="true" />
                <span>Privacy mode: most-restrictive (locked)</span>
              </div>
            </section>
          </div>
        </div>
      </IonContent>

      <IonFooter className="preset-create-footer">
        <IonToolbar>
          <div className="preset-modal-actions">
            <IonButton
              fill="outline"
              onClick={onDismiss}
              disabled={isSubmitting}
            >
              Cancel
            </IonButton>
            <IonButton
              color="secondary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save changes"}
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </>
  );
}
